import { useCallback, useEffect, useState } from "react";
import { ConversationData } from "../interfaces/all";

interface OnlineStatus {
  isOnline: boolean;
  lastSeen: Date | null;
  lastActivity: Date | null;
}

interface UseOnlineStatusProps {
  conversation: ConversationData | null;
}

export const useOnlineStatus = ({ conversation }: UseOnlineStatusProps) => {
  const [onlineStatus, setOnlineStatus] = useState<OnlineStatus>({
    isOnline: false,
    lastSeen: null,
    lastActivity: null,
  });

  // Simular status online baseado na última atividade
  const calculateOnlineStatus = useCallback(
    (conv: ConversationData): OnlineStatus => {
      if (!conv.last_message?.timestamp) {
        return {
          isOnline: false,
          lastSeen: null,
          lastActivity: null,
        };
      }

      const lastMessageTime = new Date(conv.last_message.timestamp);

      if (isNaN(lastMessageTime.getTime())) {
        return {
          isOnline: false,
          lastSeen: null,
          lastActivity: null,
        };
      }

      const now = new Date();
      const timeDiff = now.getTime() - lastMessageTime.getTime();
      const fiveMinutes = 5 * 60 * 1000; // 5 minutos

      // Considerar online se a última mensagem foi há menos de 5 minutos
      const isOnline = timeDiff < fiveMinutes;

      return {
        isOnline,
        lastSeen: isOnline ? null : lastMessageTime,
        lastActivity: lastMessageTime,
      };
    },
    [],
  );

  useEffect(() => {
    if (!conversation) {
      setOnlineStatus({
        isOnline: false,
        lastSeen: null,
        lastActivity: null,
      });
      return;
    }

    const status = calculateOnlineStatus(conversation);
    setOnlineStatus(status);

    // Atualizar status a cada minuto
    const interval = setInterval(() => {
      const updatedStatus = calculateOnlineStatus(conversation);
      setOnlineStatus(updatedStatus);
    }, 60000); // 1 minuto

    return () => clearInterval(interval);
  }, [conversation, calculateOnlineStatus]);

  // Cleanup adequado para evitar vazamentos de memória
  useEffect(() => {
    return () => {
      // Resetar estado ao desmontar
      setOnlineStatus({
        isOnline: false,
        lastSeen: null,
        lastActivity: null,
      });
    };
  }, []);

  return onlineStatus;
};
